import { useEffect, useRef, type ReactNode } from 'react';

export type PanelTab = 'files' | 'artifacts';

interface Props {
  tab: PanelTab;
  onTab: (tab: PanelTab) => void;
  counts: Record<PanelTab, number>;
  /** Room for a preview: an artifact is open. */
  wide?: boolean;
  onClose: () => void;
  children: ReactNode;
}

const LABEL: Record<PanelTab, string> = { files: 'Files', artifacts: 'Artifacts' };

/**
 * The drawer the Files and Artifacts tabs share. It owns everything about
 * being a drawer -- closing on Esc and on a click elsewhere -- so the tabs
 * only have to be lists.
 */
export function SidePanel({ tab, onTab, counts, wide, onClose, children }: Props) {
  const panelRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // A click anywhere outside closes it, like a menu. The top-bar buttons are
  // left to toggle on their own -- closing here first would reopen it -- a
  // toast is not "somewhere else", and an artifact card in the transcript
  // opens the panel rather than dismissing it.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target || panelRef.current?.contains(target)) return;
      if (target.closest('.topbar__files, .toasts, .artifact-card')) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <aside className={`files${wide ? ' files--wide' : ''}`} aria-label={LABEL[tab]} ref={panelRef}>
      <div className="files__head">
        <div className="panel-tabs" role="tablist">
          {(['artifacts', 'files'] as const).map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              className="panel-tabs__tab"
              onClick={() => onTab(t)}
            >
              {LABEL[t]} <span className="files__count">{counts[t]}</span>
            </button>
          ))}
        </div>
        <button className="share__close" ref={closeRef} onClick={onClose} aria-label="Close panel">
          ×
        </button>
      </div>
      {children}
    </aside>
  );
}
